import type {
  MarginCenterCustomerRelationshipChanges,
  MarginCenterCustomerRelationshipCoverage,
} from '../types/marginCenter';


export type MarginCenterEconomicDirection =
  | 'up'
  | 'down'
  | 'unchanged'
  | 'not_evaluable';

export type MarginCenterEconomicReadingKey =
  | 'revenue_margin'
  | 'price_units'
  | 'cost_margin';

export type MarginCenterEconomicMetricKey =
  | 'revenue'
  | 'margin_percentage'
  | 'average_net_unit_price'
  | 'units'
  | 'average_unit_cost';

export type MarginCenterEconomicMetricUnit =
  | 'percent'
  | 'percentage_points';


export interface MarginCenterEconomicMetricMovement {
  key: MarginCenterEconomicMetricKey;
  label: string;

  value: number | null;
  unit: MarginCenterEconomicMetricUnit;

  direction: MarginCenterEconomicDirection;
}


export interface MarginCenterEconomicReading {
  key: MarginCenterEconomicReadingKey;
  title: string;

  evaluable: boolean;
  hasMovement: boolean;
  summary: string;

  primary: MarginCenterEconomicMetricMovement;
  secondary: MarginCenterEconomicMetricMovement;

  contextNote: string | null;
}


interface EconomicMetricCopy {
  subject: string;
  up: string;
  down: string;
  unchanged: string;
}


const ECONOMIC_METRIC_COPY:
  Record<
    MarginCenterEconomicMetricKey,
    EconomicMetricCopy
  > = {
    revenue: {
      subject: 'los ingresos',
      up: 'suben',
      down: 'bajan',
      unchanged: 'se mantienen sin variación',
    },

    margin_percentage: {
      subject: 'el margen porcentual',
      up: 'sube',
      down: 'baja',
      unchanged: 'se mantiene sin variación',
    },

    average_net_unit_price: {
      subject: 'el precio neto medio',
      up: 'sube',
      down: 'baja',
      unchanged: 'se mantiene sin variación',
    },

    units: {
      subject: 'la cantidad vendida',
      up: 'sube',
      down: 'baja',
      unchanged: 'se mantiene sin variación',
    },

    average_unit_cost: {
      subject: 'el coste unitario medio',
      up: 'sube',
      down: 'baja',
      unchanged: 'se mantiene sin variación',
    },
  };


function getEconomicDirection(
  value: number | null | undefined,
): MarginCenterEconomicDirection {
  if (
    value === null
    || value === undefined
    || !Number.isFinite(value)
  ) {
    return 'not_evaluable';
  }

  if (value > 0) {
    return 'up';
  }

  if (value < 0) {
    return 'down';
  }

  return 'unchanged';
}


function buildMetricMovement(
  key: MarginCenterEconomicMetricKey,
  label: string,
  value: number | null | undefined,
  unit: MarginCenterEconomicMetricUnit,
): MarginCenterEconomicMetricMovement {
  const normalizedValue =
    value !== null
    && value !== undefined
    && Number.isFinite(value)
      ? value
      : null;

  return {
    key,
    label,
    value: normalizedValue,
    unit,
    direction:
      getEconomicDirection(
        normalizedValue,
      ),
  };
}


function describeMetricMovement(
  metric: MarginCenterEconomicMetricMovement,
): string | null {
  if (metric.direction === 'not_evaluable') {
    return null;
  }

  const copy =
    ECONOMIC_METRIC_COPY[metric.key];

  switch (metric.direction) {
    case 'up':
      return `${copy.subject} ${copy.up}`;

    case 'down':
      return `${copy.subject} ${copy.down}`;

    case 'unchanged':
      return `${copy.subject} ${copy.unchanged}`;

    default:
      return null;
  }
}


function capitalizeSentence(
  value: string,
): string {
  if (!value) {
    return value;
  }

  return (
    value.charAt(0).toUpperCase()
    + value.slice(1)
  );
}


function getCostCoverageNote(
  coverage:
    | MarginCenterCustomerRelationshipCoverage
    | null,
): string | null {
  if (!coverage) {
    return null;
  }

  const currentCoverage =
    coverage.current.cost_coverage_pct;

  const comparisonCoverage =
    coverage.comparison
      ?.cost_coverage_pct
    ?? null;

  const currentIsPartial =
    currentCoverage < 100;

  const comparisonIsPartial =
    comparisonCoverage !== null
    && comparisonCoverage < 100;

  if (
    currentIsPartial
    && comparisonIsPartial
  ) {
    return (
      'La cobertura de coste es parcial '
      + 'en el periodo actual y en el comparado.'
    );
  }

  if (currentIsPartial) {
    return (
      'La cobertura de coste es parcial '
      + 'en el periodo actual.'
    );
  }

  if (comparisonIsPartial) {
    return (
      'La cobertura de coste es parcial '
      + 'en el periodo comparado.'
    );
  }

  return null;
}


function buildEconomicReading({
  key,
  title,
  primary,
  secondary,
  hasComparison,
  contextNote,
}: {
  key: MarginCenterEconomicReadingKey;
  title: string;

  primary: MarginCenterEconomicMetricMovement;
  secondary: MarginCenterEconomicMetricMovement;

  hasComparison: boolean;
  contextNote: string | null;
}): MarginCenterEconomicReading {
  const evaluable =
    primary.direction !== 'not_evaluable'
    && secondary.direction !== 'not_evaluable';

  const hasMovement =
    evaluable
    && (
      primary.direction !== 'unchanged'
      || secondary.direction !== 'unchanged'
    );

  if (!hasComparison) {
    return {
      key,
      title,
      evaluable: false,
      hasMovement: false,
      summary:
        'Sin periodo comparado disponible para esta lectura.',
      primary,
      secondary,
      contextNote,
    };
  }

  if (!evaluable) {
    return {
      key,
      title,
      evaluable: false,
      hasMovement: false,
      summary:
        'No existe una base comparable válida para ambas métricas.',
      primary,
      secondary,
      contextNote,
    };
  }

  const primaryDescription =
    describeMetricMovement(primary);

  const secondaryDescription =
    describeMetricMovement(secondary);

  if (
    !primaryDescription
    || !secondaryDescription
  ) {
    return {
      key,
      title,
      evaluable: false,
      hasMovement: false,
      summary:
        'No existe una base comparable válida para ambas métricas.',
      primary,
      secondary,
      contextNote,
    };
  }

  return {
    key,
    title,
    evaluable: true,
    hasMovement,
    summary: capitalizeSentence(
      `${primaryDescription} y `
      + `${secondaryDescription} `
      + 'frente al periodo comparado.',
    ),
    primary,
    secondary,
    contextNote,
  };
}


export function buildCustomerProductEconomicReadings({
  changes,
  coverage,
}: {
  changes:
    | MarginCenterCustomerRelationshipChanges
    | null;

  coverage:
    | MarginCenterCustomerRelationshipCoverage
    | null;
}): MarginCenterEconomicReading[] {
  const revenue =
    buildMetricMovement(
      'revenue',
      'Ingresos',
      changes?.revenue_delta_pct,
      'percent',
    );

  const margin =
    buildMetricMovement(
      'margin_percentage',
      'Margen %',
      changes
        ?.margin_percentage_delta_points,
      'percentage_points',
    );

  const price =
    buildMetricMovement(
      'average_net_unit_price',
      'Precio neto medio',
      changes
        ?.average_net_unit_price_delta_pct,
      'percent',
    );

  const units =
    buildMetricMovement(
      'units',
      'Cantidad',
      changes?.units_delta_pct,
      'percent',
    );

  const cost =
    buildMetricMovement(
      'average_unit_cost',
      'Coste unitario medio',
      changes
        ?.average_unit_cost_delta_pct,
      'percent',
    );

  const coverageNote =
    getCostCoverageNote(coverage);

  const hasComparison =
    changes !== null;

  return [
    buildEconomicReading({
      key: 'revenue_margin',
      title: 'Ingresos + margen',
      primary: revenue,
      secondary: margin,
      hasComparison,
      contextNote: coverageNote,
    }),

    buildEconomicReading({
      key: 'price_units',
      title: 'Precio + cantidad',
      primary: price,
      secondary: units,
      hasComparison,
      contextNote: null,
    }),

    buildEconomicReading({
      key: 'cost_margin',
      title: 'Coste + margen',
      primary: cost,
      secondary: margin,
      hasComparison,
      contextNote: coverageNote,
    }),
  ];
}